import type {
  AnswerRateRequest,
  AnswerRateResponse,
  ApiError,
  ExtensionComparisonRequest,
  ExtensionComparisonResponse,
  NumberComparisonRequest,
  NumberComparisonResponse,
  PhoneNumber,
} from '@mangovoice/types'

import type {
  PaginatedQueuesResponse,
  QueueAgentsResponse,
  QueueCallsResponse,
  QueueDashboardCustomStatus,
  QueueDashboardExtension,
  QueueMetricsApiResponse,
} from '../pages/QueueDashboard/types'

export interface ExtensionLineEntry {
  number: string
  name: string
}

export function normalizeExtensionLines(
  raw:
    ExtensionLineEntry[] | { results: ExtensionLineEntry[] } | null | undefined,
): ExtensionLineEntry[] {
  if (Array.isArray(raw)) return raw
  return raw?.results ?? []
}

interface ApiResponse<T> {
  data: T | null
  ok: boolean
  status?: number
  problem?: string | null
}

export interface ReportingApiClient {
  getAnswerRate: (
    request: AnswerRateRequest,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<AnswerRateResponse>>

  getNumberComparison: (
    request: NumberComparisonRequest,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<NumberComparisonResponse>>

  getExtensionComparison: (
    request: ExtensionComparisonRequest,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<ExtensionComparisonResponse>>

  getExtensionLines: (params: {
    pbx_id: number
  }) => Promise<ApiResponse<ExtensionLineEntry[]>>

  getPhoneNumbers: (params: {
    pbx_id: number
    limit?: number
    offset?: number
  }) => Promise<ApiResponse<{ results: PhoneNumber[]; count: number }>>

  getSystemSettings: (params: {
    pbx_id: number
  }) => Promise<ApiResponse<{ default_time_zone?: string }>>

  getQueueDashboardQueues: (
    pbxId: number,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<PaginatedQueuesResponse>>
  getQueueDashboardCalls: (
    pbxUuid: string,
    queueNumber: string,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<QueueCallsResponse>>
  getQueueDashboardAgents: (
    pbxUuid: string,
    queueNumber: string,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<QueueAgentsResponse>>
  getQueueMetrics: (
    params: { start: string; end: string; pbxes: string; queue: string },
    signal?: AbortSignal,
  ) => Promise<ApiResponse<QueueMetricsApiResponse>>

  getQueueDashboardCustomStatuses?: (
    pbxId: number,
  ) => Promise<ApiResponse<{ results: QueueDashboardCustomStatus[] }>>
  getQueueDashboardLineExtensions?: (
    pbxId: number,
  ) => Promise<ApiResponse<{ results: QueueDashboardExtension[] }>>
  queueDashboardLoginAgent?: (
    extensionId: number,
  ) => Promise<ApiResponse<undefined>>
  queueDashboardLogoutAgent?: (
    extensionId: number,
  ) => Promise<ApiResponse<undefined>>
  updateQueueDashboardAgentStatus?: (
    extUuid: string,
    customStatusId: string | null,
    inQueue?: boolean,
  ) => Promise<ApiResponse<undefined>>
}

export type QueueDashboardApiError = ApiError

export type ReportingApi = ReportingApiClient

/**
 * @deprecated No-op pass-through kept for backward compatibility.
 * Consumers should use `ReportingApiClient` / `ReportingApi` directly.
 * Will be removed once apps are migrated to `@mangovoice/mango-api-client`.
 */
export const createReportingApi = (client: ReportingApiClient): ReportingApi =>
  client
